/**
 * Maintenance mode Redux module.
 *
 * Tracks whether the backend is in maintenance mode and surfaces the
 * window details to the UI.  This is pure client-side state — it is
 * populated by the HTTP response interceptor in baseApi and cleared
 * automatically when the maintenance window expires.
 *
 * Action-type prefix: MAINTENANCE_*
 */

export const MAINTENANCE_SET = 'MAINTENANCE_SET';
export const MAINTENANCE_CLEAR = 'MAINTENANCE_CLEAR';

/**
 * setMaintenance
 *
 * Dispatched by the baseApi interceptor when a 503 with
 * error_code === 'MAINTENANCE_MODE' is received.
 *
 * @param {Object} payload
 * @param {string} payload.message   - Human-readable message from the API
 * @param {string|null} payload.starts_at - ISO-8601 UTC start time
 * @param {string|null} payload.ends_at   - ISO-8601 UTC end time
 */
export const setMaintenance = payload => ({ type: MAINTENANCE_SET, payload });

/**
 * clearMaintenance
 *
 * Dispatched when the maintenance window has elapsed (either by the
 * banner's local timer or by the interceptor detecting a successful
 * response after ends_at has passed).
 */
export const clearMaintenance = () => ({ type: MAINTENANCE_CLEAR });

const DEFAULT_STATE = {
  active: false,
  starts_at: null,
  ends_at: null,
  message: null
};

const maintenanceReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case MAINTENANCE_SET:
      return {
        ...state,
        active: true,
        starts_at: action.payload.starts_at || null,
        ends_at: action.payload.ends_at || null,
        message: action.payload.message || null
      };

    case MAINTENANCE_CLEAR:
      return { ...DEFAULT_STATE };

    default:
      return state;
  }
};

/* Selectors */
export const selectMaintenanceActive = state => state.maintenance.active;
export const selectMaintenanceStartsAt = state => state.maintenance.starts_at;
export const selectMaintenanceEndsAt = state => state.maintenance.ends_at;
export const selectMaintenanceMessage = state => state.maintenance.message;

export default maintenanceReducer;
